import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import {
  CHAT_DEFAULT_LIVE_BAR_FETCH_PARAMS,
  CHAT_LIVE_BAR_ELEMENTS_COUNT,
} from "chat/constants";
import { useEmblaScrollState } from "chat/hooks/useEmblaScrollState";
import { ChevronLeftIcon, ChevronRightIcon } from "chat/imports/assets";
import { Button } from "chat/imports/components";
import { Breakpoints } from "chat/imports/constants";
import { useBreakpointPrecise } from "chat/imports/hooks";
import { RootState } from "chat/imports/state";
import { ButtonSize, ButtonVariant } from "chat/imports/types";
import { LiveBarCarouselTrack } from "chat/liveBar/components/LiveBarCarouselTrack/LiveBarCarouselTrack";
import { fetchLiveBarRequest } from "chat/liveBar/state/asyncAction";
import { liveBarSelectors } from "chat/liveBar/state/selectors";
import { getChatLiveBarModerationLevel } from "chat/soc/chatSoc";
import styles from "./LiveBarCarousel.scss";

interface LiveBarCarouselProps {
  className?: string;
}

const selector = (state: RootState) => ({
  streamList: liveBarSelectors.getStreamList(state),
  hasMore: liveBarSelectors.getHasMore(state),
  lastTimestamp: liveBarSelectors.getLastTimestamp(state),
  moderationLevel: getChatLiveBarModerationLevel(state),
});

export const LiveBarCarousel: React.FC<LiveBarCarouselProps> = ({
  className,
}) => {
  const dispatch = useDispatch();
  const breakpoint = useBreakpointPrecise();
  const [isCarouselActive, setIsCarouselActive] = useState(true);
  const isMobile = breakpoint !== Breakpoints.DESKTOP;

  const { streamList, hasMore, lastTimestamp, moderationLevel } = useSelector(
    selector,
    shallowEqual
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    skipSnaps: true,
    watchDrag: isMobile,
    active: isCarouselActive,
  });

  const { canScrollPrev, canScrollNext } = useEmblaScrollState(emblaApi);

  useEffect(() => {
    if (hasMore && !canScrollNext) {
      dispatch(
        fetchLiveBarRequest({
          ...CHAT_DEFAULT_LIVE_BAR_FETCH_PARAMS,
          refresh: false,
          moderation_level: moderationLevel,
          last_timestamp: lastTimestamp,
        })
      );
    }
  }, [
    hasMore,
    canScrollNext,
    streamList,
    dispatch,
    lastTimestamp,
    moderationLevel,
  ]);

  useEffect(() => {
    const currentIndex = emblaApi?.selectedScrollSnap();
    if ((!currentIndex || currentIndex === 0) && isMobile) {
      setIsCarouselActive(canScrollNext);
    }
  }, [canScrollNext]);

  const handleNextClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    const currentIndex = emblaApi.selectedScrollSnap();
    const totalSnaps = emblaApi.scrollSnapList().length;
    const nextIndex = Math.min(currentIndex + 2, totalSnaps - 1);
    emblaApi.scrollTo(nextIndex);
  }, [emblaApi]);

  const handlePrevClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    const currentIndex = emblaApi.selectedScrollSnap();
    const prevIndex = Math.max(currentIndex - 2, 0);
    emblaApi.scrollTo(prevIndex);
  }, [emblaApi]);

  const showNavigation = streamList?.length > CHAT_LIVE_BAR_ELEMENTS_COUNT;

  return (
    <div
      className={classnames(
        styles.liveBarCarouselContainer,
        className,
        styles[breakpoint],
        {
          [styles.withoutPadding]: !showNavigation,
        }
      )}
    >
      {streamList?.length > 0 && (
        <div
          className={classnames(styles.liveBarCarouselWrapper, {
            [styles.canScrollPrev]: true,
            [styles.withoutNavigation]: !showNavigation,
          })}
          ref={emblaRef}
        >
          <LiveBarCarouselTrack data={streamList} />
        </div>
      )}

      {showNavigation && (
        <>
          <Button
            className={styles.prevSlideButton}
            size={ButtonSize.CIRCLE_MEDIUM_40}
            variant={ButtonVariant.SECONDARY}
            onClick={handlePrevClick}
            disabled={!canScrollPrev}
            dataTestId="live-bar-prev-carousel-arrow"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            className={styles.nextSlideButton}
            size={ButtonSize.CIRCLE_MEDIUM_40}
            variant={ButtonVariant.SECONDARY}
            onClick={handleNextClick}
            disabled={!canScrollNext}
            dataTestId="live-bar-next-carousel-arrow"
          >
            <ChevronRightIcon />
          </Button>
        </>
      )}
    </div>
  );
};
